import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import {
  useCohortBuilderData,
  usePostCohortExecutionMutation,
} from '@verantos-dev/data-access-cohorts';
import { emitUserSaveEvent } from '@verantos-dev/feature-research-assistant';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-cohorts';

import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useCohortBuilderSaveQuery } from '../../hooks/common/useCohortBuilderSaveQuery';
import { getCohortEditModalTitle } from '../../utils/getCohortEditModalTitle';

export const useProcessCohortDefinitionModal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { cohortId, latestCohortVersionId } = useCohortBuilderData();
  const { closeDialog } = useCohortBuilderModalContext();
  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { user } = useAuth();

  const [
    postCohortExecution,
    { isLoading: isPostCohortExecutionLoading, error: postCohortExecutionError },
  ] = usePostCohortExecutionMutation();

  const {
    projectId,
    cohortData,
    submitVersion,
    isInitialLoading,
    initialLoadingError,
    isSubmitting,
    submittingError,
    isSubmitSuccess,
  } = useCohortBuilderSaveQuery(postCohortExecution);

  const cohortBuilderPath = projectId
    ? cohortId && latestCohortVersionId
      ? `${getCohortBuilderPath(projectId, cohortId, latestCohortVersionId)}?pageTab=${
          CohortBuilderPageTab.results
        }` // route to cohort builder if cohort id
      : getCohortsPath(projectId) // route to cohorts list if no cohort id
    : getProjectsBasePath(); // route to projects list if no project id

  const onClose = () => {
    if (!isPostCohortExecutionLoading) {
      closeDialog();
      isSubmitSuccess && navigate(cohortBuilderPath);
    }
  };

  const onRedirectAfterSubmit = () => {
    closeDialog();
    navigate(cohortBuilderPath);
  };

  const onSubmit = () => {
    submitVersion();
    if (projectId && cohortId && latestCohortVersionId) {
      emitUserSaveEvent({
        model: researchAssistantModel,
        cohortVersionId: latestCohortVersionId,
        cohortId,
        projectId,
        user,
        cohortAction: COHORT_ACTIONS.PROCESS,
      });
    }
    dispatch(
      addResearchAssistantMessages([
        {
          message: intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.processedInfoMessage',
          }),
          timestamp: new Date().toISOString(),
          currentUser: 'user',
          isCurrentUser: true,
          type: ResearchAssistantMessageEnumLegacy.PROCESS,
        },
      ])
    );
  };

  return {
    title: getCohortEditModalTitle(
      intl,
      CohortBuilderActionDialogType.PROCESS_COHORT_DEFINITION,
      true
    ),
    onClose,
    onSubmit,
    onRedirectAfterSubmit,
    cohortName: cohortData?.data?.cohortName,
    isSubmitting,
    submittingError,
    isPostCohortExecutionLoading,
    initialLoadingError,
    latestStatus: cohortData?.data?.latestStatus,
    postCohortExecutionError,
    isInitialLoading,
  };
};
